import sentinelNetwork from './sentinelNetwork';

export interface CategoryFindings {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface CategoryRow extends CategoryFindings {
  key: string;
  label: string;
}

export interface DomainGroup {
  domain: string;
  projectCount: number;
  avgScore: number | null;
  green: number;
  amber: number;
  red: number;
  notScanned: number;
  criticalFindings: number;
}

export interface ProjectScoreRow {
  appId: string;
  appName: string;
  leanIXId: string;
  domain: string;
  criticality: string | null;
  score: number | null;
  rag: 'GREEN' | 'AMBER' | 'RED' | null;
  scoreDelta: number | null;
  criticalFindings: number;
  findingsByCategory: Record<string, CategoryFindings>;
  toolCoverage: { connected: string[]; not_connected: string[]; coverage_pct: number } | null;
  repoCount: number;
  lastScanAt: string | null;
  createdAt: string | null;
  stale: boolean;
  scanned: boolean;
  managementLevel?: string;
}

export interface TopAttentionRow {
  appId: string;
  appName: string;
  domain: string;
  score: number | null;
  rag: string | null;
  criticalFindings: number;
  scoreDelta: number | null;
}

export interface ActivityItem {
  type: 'onboarded' | 'scan' | 'score';
  appId: string;
  appName: string;
  at: string;
}

export interface ManagementOverview {
  success: boolean;
  scope: 'all' | 'assigned';
  generatedAt: string;
  kpis: {
    totalProjects: number;
    scannedProjects: number;
    notScannedProjects: number;
    newThisMonth: number;
    avgScore: number | null;
    prevAvgScore: number | null;
    criticalProjects: number;
    criticalFindings: number;
    improving: number;
    needsAttention: number;
  };
  healthDistribution: { green: number; amber: number; red: number; notScanned: number };
  findingsByCategory: CategoryRow[];
  domainGroups: DomainGroup[];
  topAttention: TopAttentionRow[];
  scanStatus: { scanned: number; notScanned: number; toolCoverage: Record<string, number> };
  activity: ActivityItem[];
  projects: ProjectScoreRow[];
}

export interface ManagementTrends {
  success: boolean;
  months: number;
  trends: Array<{
    month: string;
    avgScore: number | null;
    criticalFindings: number;
    appsWithData: number;
  }>;
  appCount: number;
}

export interface HierarchyNode {
  email: string;
  level: string;
  projects: Array<{ appId: string; appName: string }>;
  subtreeProjectCount: number;
  children: HierarchyNode[];
}

export interface ManagementHierarchy {
  success: boolean;
  scope: 'all' | 'assigned';
  generatedAt: string;
  roots: HierarchyNode[];
}

export const getManagementHierarchy = () =>
  sentinelNetwork.get<ManagementHierarchy>('/config/management/hierarchy');

export const getManagementOverview = () =>
  sentinelNetwork.get<ManagementOverview>('/config/management/overview');

export const getManagementTrends = (months = 6) =>
  sentinelNetwork.get<ManagementTrends>('/config/management/trends', { params: { months } });
